import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { SAR } from "../components/RiyalIcon";
import {
  Car,
  Store,
  Bike,
  Phone,
  Globe,
  ArrowLeft,
  RefreshCw,
  Sparkles,
  MapPin,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  NEW: "جديد", PREPARING: "جاري التجهيز", READY: "جاهز",
  OUT_FOR_DELIVERY: "خرج للتوصيل", CUSTOMER_ARRIVED: "العميل وصل",
  COMPLETED: "تم التسليم", CANCELLED: "ملغي",
};
const NEXT_STATE = (o) => {
  if (o.pickup_method === "DELIVERY") {
    return { NEW: "PREPARING", PREPARING: "READY", READY: "OUT_FOR_DELIVERY", OUT_FOR_DELIVERY: "COMPLETED" }[o.status];
  }
  if (o.status === "CUSTOMER_ARRIVED") return "COMPLETED";
  return { NEW: "PREPARING", PREPARING: "READY", READY: "COMPLETED" }[o.status];
};
const NEXT_LABEL = { PREPARING: "ابدأ التجهيز", READY: "تم التجهيز", OUT_FOR_DELIVERY: "خرج للتوصيل", COMPLETED: "تم التسليم" };

export default function DemoOwner() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("ACTIVE");

  const load = () => api.get("/demo/orders").then(r => setOrders(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, []);

  const advance = async (o) => {
    const next = NEXT_STATE(o); if (!next) return;
    try { await api.patch(`/demo/orders/${o.id}/status`, { status: next }); load(); toast.success("تم التحديث"); }
    catch { toast.error("تعذر التحديث"); }
  };

  const active = ["NEW","PREPARING","READY","OUT_FOR_DELIVERY","CUSTOMER_ARRIVED"];
  const visible = orders.filter(o => filter === "ALL" ? true : filter === "ACTIVE" ? active.includes(o.status) : o.status === filter);
  const arrived = orders.filter(o => o.status === "CUSTOMER_ARRIVED");
  const counts = {
    NEW: orders.filter(o => o.status === "NEW").length,
    PREPARING: orders.filter(o => o.status === "PREPARING").length,
    READY: orders.filter(o => ["READY","OUT_FOR_DELIVERY"].includes(o.status)).length,
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-orange-100">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <Link to="/demo" className="w-10 h-10 rounded-xl bg-orange-50 grid place-items-center" data-testid="back-demo"><ArrowLeft size={20} className="rotate-180"/></Link>
            <div>
              <div className="text-xs text-orange-700 font-bold flex items-center gap-1"><Sparkles size={12}/> نسخة تجريبية</div>
              <div className="font-extrabold">لوحة المحل</div>
            </div>
          </div>
          <button onClick={load} className="pill bg-white border border-orange-100 px-3 py-2" data-testid="refresh-btn"><RefreshCw size={16}/></button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-4">
        <div className="grid grid-cols-3 gap-2 mb-4">
          <Stat label="جديد" value={counts.NEW} tone="orange" />
          <Stat label="جاري التجهيز" value={counts.PREPARING} tone="amber" />
          <Stat label="جاهزة" value={counts.READY} tone="green" />
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
          {[["ACTIVE","النشطة"],["NEW","جديد"],["PREPARING","جاري التجهيز"],["READY","جاهز"],["CUSTOMER_ARRIVED","العميل وصل"],["COMPLETED","مكتملة"],["ALL","الكل"]].map(([k,l]) => (
            <button key={k} onClick={() => setFilter(k)} className={`pill whitespace-nowrap px-3 py-1.5 text-xs font-bold border ${filter === k ? "brand-grad text-white border-transparent" : "bg-white text-gray-700 border-gray-200"}`}>{l}</button>
          ))}
        </div>

        {arrived.map(o => (
          <div key={o.id} className="mt-3 rounded-2xl border-2 border-green-500 bg-green-50 p-4 rise-in" data-testid={`arrived-alert-${o.order_number}`}>
            <div className="flex items-center gap-2 text-green-800 font-extrabold text-lg">🚗 العميل وصل</div>
            <div className="mt-1 text-sm">طلب <span className="num-badge font-bold">#{o.order_number}</span> — {o.car_type} {o.car_color} <span className="num-badge">{o.car_plate}</span></div>
            <button onClick={() => advance(o)} className="mt-3 w-full pill bg-green-600 text-white font-bold py-3 tap-target">تم تسليم الطلب</button>
          </div>
        ))}

        <div className="mt-4 space-y-3">
          {visible.map(o => <OrderCard key={o.id} o={o} onAdvance={advance} />)}
          {visible.length === 0 && <div className="text-center text-gray-500 py-10">لا توجد طلبات</div>}
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const cls = { orange: "text-orange-700 bg-orange-50 border-orange-100", amber: "text-amber-700 bg-amber-50 border-amber-100", green: "text-green-700 bg-green-50 border-green-100" }[tone];
  return <div className={`rounded-2xl border p-3 ${cls}`}><div className="text-xs">{label}</div><div className="text-2xl font-extrabold num-badge">{value}</div></div>;
}
function OrderCard({ o, onAdvance }) {
  const next = NEXT_STATE(o);
  const pickupIcon = o.pickup_method === "CAR" ? <Car size={14}/> : o.pickup_method === "STORE" ? <Store size={14}/> : <Bike size={14}/>;
  const pickupLabel = o.pickup_method === "CAR" ? "استلام عند السيارة" : o.pickup_method === "STORE" ? "استلام من المحل" : "توصيل";
  const isDone = o.status === "COMPLETED" || o.status === "CANCELLED";
  const hasDeliveryLocation = o.pickup_method === "DELIVERY"
    && o.delivery_latitude !== null
    && o.delivery_latitude !== undefined
    && o.delivery_longitude !== null
    && o.delivery_longitude !== undefined
    && Number.isFinite(Number(o.delivery_latitude))
    && Number.isFinite(Number(o.delivery_longitude));
  const mapsUrl = hasDeliveryLocation
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      `${o.delivery_latitude},${o.delivery_longitude}`,
    )}`
    : "";
  return (
    <div className="bg-white rounded-2xl border border-orange-100 card-shadow p-4" data-testid={`order-${o.order_number}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 font-extrabold">🔔 طلب <span className="num-badge">#{o.order_number}</span></div>
        <span className={`pill text-xs font-bold px-2.5 py-1 ${o.status === "NEW" ? "bg-orange-100 text-orange-800" : o.status === "PREPARING" ? "bg-amber-100 text-amber-800" : o.status === "READY" ? "bg-green-100 text-green-800" : o.status === "CUSTOMER_ARRIVED" ? "bg-green-600 text-white" : "bg-gray-100 text-gray-700"}`}>{STATUS_LABEL[o.status]}</span>
      </div>
      <div className="mt-2 text-sm space-y-0.5">
        {o.items.map((it, i) => <div key={i} className="flex justify-between"><span>{it.name} × {it.quantity}</span><span className="num-badge text-gray-500"><SAR>{it.line_total}</SAR></span></div>)}
      </div>
      <div className="mt-2 pt-2 border-t flex justify-between text-sm">
        <span className="text-gray-500">الإجمالي</span>
        <span className="font-extrabold text-orange-600"><SAR>{o.total}</SAR></span>
      </div>
      <div className="mt-3 text-xs text-gray-600 space-y-1">
        <div className="inline-flex items-center gap-1">{pickupIcon} {pickupLabel}</div>
        {o.pickup_method === "CAR" && <div>{o.car_type} — {o.car_color} — <span className="num-badge">{o.car_plate}</span></div>}
        {o.pickup_method === "DELIVERY" && <div>📍 {o.delivery_address}</div>}
        {hasDeliveryLocation && (
          <div className="pt-1">
            <div className="inline-flex items-center gap-1" data-testid={`demo-delivery-location-${o.order_number}`}>
              <MapPin size={13} />
              موقع التوصيل: {Number(o.delivery_latitude).toFixed(6)}, {Number(o.delivery_longitude).toFixed(6)}
            </div>
            <a
              href={mapsUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-flex items-center gap-1 font-bold text-orange-700"
              data-testid={`demo-open-google-maps-${o.order_number}`}
            >
              <ExternalLink size={13} />
              فتح في خرائط Google
            </a>
          </div>
        )}
        <div>{o.source === "PHONE" ? <><Phone size={12} className="inline"/> طلب هاتفي</> : <><Globe size={12} className="inline"/> طلب من الموقع</>} · {o.customer_name || "بدون اسم"} — {o.customer_phone}</div>
      </div>
      {!isDone && next && o.status !== "CUSTOMER_ARRIVED" && (
        <button onClick={() => onAdvance(o)} className="mt-3 w-full pill brand-grad text-white font-bold py-3 tap-target">{NEXT_LABEL[next]}</button>
      )}
    </div>
  );
}
